class CampaignLookbook extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.nav = this.querySelector('[data-lookbook-nav]');
    this.rail = this.nav?.querySelector('[data-lookbook-rail]') || this.nav;
    this.navButtons = Array.from(this.nav?.querySelectorAll('[data-lookbook-scroll]') || []);
    const navigationTemplates = Array.from(this.querySelectorAll('template[data-lookbook-navigation]'));
    if (navigationTemplates.length && this.rail) {
      const destinations = new Set();
      const links = navigationTemplates.flatMap(template => {
        const link = template.content.firstElementChild;
        const target = link && this.targetForLink(link);
        if (!target || destinations.has(target)) return [];
        destinations.add(target);
        return [link.cloneNode(true)];
      });
      this.rail.replaceChildren(...links);
      this.nav.hidden = links.length === 0;
    }
    this.links = Array.from(this.nav?.querySelectorAll('[data-lookbook-link]') || []);
    const candidates = new Map(Array.from(this.querySelectorAll('[data-lookbook-panel]'), panel => [panel.id, panel]));
    this.panels = new Map();
    this.links.forEach(link => {
      const target = this.targetForLink(link);
      if (candidates.has(target)) this.panels.set(target, candidates.get(target));
    });
    this.placeEditorials();
    this.parallaxLayers = Array.from(this.querySelectorAll('[data-lookbook-parallax]'));
    this.revealScope = this.closest('main') || this;
    this.revealElements = new Set();
    this.observedElements = new Set();
    this.queuedEntrances = new Map();
    this.seen = new WeakSet();
    this.motionReleased = !document.documentElement?.hasAttribute('data-halloween-incoming');
    const { signal } = this.lifecycle;

    this.addEventListener('click', event => this.selectFromLink(event), { signal });
    this.nav?.addEventListener('click', event => this.scrollNavigation(event), { signal });
    this.rail?.addEventListener('scroll', () => this.updateNavigationControls(), { passive: true, signal });
    this.revealScope.addEventListener('focusin', event => this.revealFocused(event.target), { signal });
    this.revealScope.addEventListener('animationend', event => {
      if (event.animationName === 'lookbook-enter' && this.revealElements.has(event.target)) this.completeEntrance(event.target);
    }, { signal });
    window.addEventListener('hashchange', () => this.activate(location.hash.slice(1) || this.defaultPanel(), true), { signal });
    window.addEventListener('scroll', () => this.queueParallax(), { passive: true, signal });
    window.addEventListener('resize', () => {
      this.updateNavigationControls();
      this.revealActiveLink();
      this.queueParallax();
    }, { passive: true, signal });
    this.motion.addEventListener('change', () => {
      this.finishEntrances();
      this.queueParallax();
    }, { signal });
    document.addEventListener('DOMContentLoaded', () => {
      this.prepareEntrances();
      this.observeEntrances();
    }, { once: true, signal });
    window.addEventListener('pagehide', () => this.finishEntrances(), { signal });
    ['shopify:section:load', 'shopify:section:reorder'].forEach(type => {
      document.addEventListener(type, () => {
        if (!this.isConnected) return;
        this.prepareEntrances();
        this.observeEntrances();
      }, { signal });
    });
    document.addEventListener('shopify:section:unload', event => {
      this.revealElements.forEach(element => { if (event.target.contains(element)) this.completeEntrance(element); });
    }, { signal });
    document.addEventListener('shopify:block:select', event => this.selectEditorBlock(event), { signal });

    const hash = location.hash.slice(1);
    this.updateNavigationControls();
    this.activate(this.panels.has(hash) ? hash : this.defaultPanel());
    if (!this.isEditor()) {
      this.prepareEntrances();
      const startMotion = () => {
        this.motionReleased = true;
        this.observeEntrances();
        this.queueParallax();
      };
      if (document.documentElement?.hasAttribute('data-halloween-incoming')) document.addEventListener('halloween:ready', startMotion, { once: true, signal });
      else startMotion();
      if (this.dataset.navigationUrl) void import(this.dataset.navigationUrl).then(({ connectCatalogOrigin }) => {
        if (!signal.aborted) connectCatalogOrigin(this, signal);
      }).catch(() => {});
    }
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = null;
    this.observer?.disconnect();
    this.observer = null;
    cancelAnimationFrame(this.navigationFrame);
    cancelAnimationFrame(this.parallaxFrame);
    this.parallaxFrame = 0;
    this.finishEntrances();
    this.parallaxLayers?.forEach(layer => layer.style.removeProperty('--lookbook-parallax-y'));
    this.panels?.forEach(panel => { panel.hidden = false; });
    this.links?.forEach(link => link.removeAttribute('aria-current'));
  }

  isEditor() {
    return this.dataset.editor === 'true' || window.Shopify?.designMode === true;
  }

  placeEditorials() {
    // Nested Shopify blocks render together; move the existing nodes to preserve DOM reading order.
    this.panels.forEach(panel => {
      const grid = panel.querySelector('.edit-mosaic');
      if (!grid) return;
      const products = Array.from(grid.children).filter(item => item.hasAttribute('data-lookbook-product'));
      const stories = Array.from(grid.children).filter(item => item.hasAttribute('data-lookbook-after'));
      const anchors = new Map();
      stories.forEach(story => {
        const position = Math.max(0, Math.floor(Number(story.dataset.lookbookAfter) || 0));
        story.hidden = position > products.length && !this.isEditor();
        if (position > products.length) return;
        const previous = anchors.get(position) || (position > 0 ? products[position - 1] : null);
        if (previous) grid.insertBefore(story, previous.nextSibling);
        else grid.insertBefore(story, products[0] || Array.from(grid.children).find(item => item.classList.contains('edit-mosaic__pair')) || null);
        anchors.set(position, story);
      });
    });
  }

  defaultPanel() {
    return this.panels.has('halloween-favoritos') ? 'halloween-favoritos' : this.panels.keys().next().value;
  }

  targetForLink(link) {
    const href = link.getAttribute('href');
    return link.getAttribute('data-lookbook-target') || (href?.startsWith('#') ? href.slice(1) : null);
  }

  selectFromLink(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('[data-lookbook-link]');
    if (!link || !this.contains(link) || link.hasAttribute('download') || link.target && link.target !== '_self') return;
    const href = link.getAttribute('href');
    const id = this.targetForLink(link);
    if (!this.panels.has(id)) return;
    const hash = `#${id}`;
    if (href !== hash) {
      if (!link.hasAttribute('data-lookbook-target') || !this.nav?.contains(link)) return;
      try {
        const destination = new URL(href, location.href);
        const current = new URL(location.href);
        if (destination.origin !== current.origin || destination.username || destination.password || destination.pathname !== current.pathname || destination.hash !== hash) return;
        const views = destination.searchParams.getAll('view');
        if (views.length !== 1 || views[0] !== current.searchParams.get('view') || Array.from(destination.searchParams.keys()).some(key => key !== 'view')) return;
      } catch { return; }
    }
    event.preventDefault();
    this.activate(id, true);
    if (location.hash !== hash) history.pushState(null, '', hash);
  }

  selectEditorBlock(event) {
    if (!this.isEditor() || !this.contains(event.target)) return;
    const panel = event.target.closest?.('[data-lookbook-panel]');
    const link = event.target.closest?.('[data-lookbook-link]') || event.target.querySelector?.('[data-lookbook-link]');
    this.activate(panel?.id || (link && this.targetForLink(link)));
  }

  activate(id, navigate = false) {
    const selected = this.panels.get(id);
    if (!selected) return;
    this.activePanel = id;
    this.panels.forEach((panel, panelId) => { panel.hidden = panelId !== id; });
    this.links.forEach(link => {
      if (this.targetForLink(link) === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    this.revealActiveLink();
    this.queueParallax();
    cancelAnimationFrame(this.navigationFrame);
    if (!navigate) return;
    this.navigationFrame = requestAnimationFrame(() => {
      if (!this.isConnected || !selected.isConnected || this.activePanel !== id) return;
      selected.querySelector('[data-lookbook-heading]')?.focus({ preventScroll: true });
      selected.scrollIntoView({ block: 'start', behavior: this.motion.matches ? 'auto' : 'smooth' });
    });
  }

  revealActiveLink() {
    if (!this.rail || this.rail.scrollWidth <= this.rail.clientWidth) return;
    const active = this.links.find(link => link.hasAttribute('aria-current'));
    if (!active) return;
    const left = active.offsetLeft - (this.rail.clientWidth - active.offsetWidth) / 2;
    this.rail.scrollTo({ left: Math.max(0, Math.min(this.rail.scrollWidth - this.rail.clientWidth, left)), behavior: 'auto' });
    this.updateNavigationControls();
  }

  scrollNavigation(event) {
    const button = event.target.closest?.('[data-lookbook-scroll]');
    if (!button || !this.nav?.contains(button) || button.disabled) return;
    const direction = button.dataset.lookbookScroll === 'previous' ? -1 : 1;
    this.rail.scrollBy({ left: direction * this.rail.clientWidth * 0.75, behavior: this.motion.matches ? 'auto' : 'smooth' });
  }

  updateNavigationControls() {
    if (!this.nav || !this.rail) return;
    const style = getComputedStyle(this.nav);
    const padding = (parseFloat(style.paddingInlineStart) || 0) + (parseFloat(style.paddingInlineEnd) || 0);
    const overflow = this.rail.scrollWidth > this.nav.clientWidth - padding + 1;
    this.navButtons.forEach(button => {
      button.hidden = !overflow;
      button.disabled = button.dataset.lookbookScroll === 'previous' ? this.rail.scrollLeft <= 1 : this.rail.scrollLeft >= this.rail.scrollWidth - this.rail.clientWidth - 1;
    });
    this.style.setProperty('--lookbook-navigation-height', `${this.nav.getBoundingClientRect().height}px`);
  }

  observeEntrances() {
    if (!this.observer || !this.motionReleased || this.motion.matches || this.isEditor()) return;
    try {
      this.revealElements.forEach(element => {
        if (this.seen.has(element) || this.observedElements.has(element)) return;
        this.observer.observe(element);
        this.observedElements.add(element);
      });
    } catch {
      this.finishEntrances();
    }
  }

  prepareEntrances() {
    if (this.isEditor() || this.motion.matches || !('IntersectionObserver' in window) || getComputedStyle(this).getPropertyValue('--lookbook-entrance-enabled').trim() !== '1') {
      this.finishEntrances();
      return;
    }
    try {
      if (!this.observer) this.observer = new IntersectionObserver(entries => {
        try { this.queueEntrances(entries); } catch { this.finishEntrances(); }
      }, { threshold: .08, rootMargin: '0px 0px 32px 0px' });
      this.revealElements.forEach(element => { if (!element.isConnected) this.completeEntrance(element); });
      this.revealScope.querySelectorAll('[data-lookbook-reveal]').forEach(element => {
        if (this.seen.has(element) || this.revealElements.has(element)) return;
        const parent = element.parentElement?.closest('[data-lookbook-reveal]');
        if (parent && this.revealScope.contains(parent)) return;
        if (element.contains(document.activeElement)) {
          this.seen.add(element);
          return;
        }
        this.revealElements.add(element);
        element.classList.add('is-prepared');
      });
    } catch {
      this.finishEntrances();
    }
  }

  queueEntrances(entries) {
    const visible = entries.filter(entry => entry.isIntersecting && this.revealElements.has(entry.target));
    visible.sort((a, b) => {
      const first = a.boundingClientRect || a.target.getBoundingClientRect();
      const second = b.boundingClientRect || b.target.getBoundingClientRect();
      return Math.abs(first.top - second.top) > 8 ? first.top - second.top : first.left - second.left;
    });
    visible.forEach((entry, index) => {
      const element = entry.target;
      this.observer.unobserve(element);
      this.observedElements.delete(element);
      if (element.contains(document.activeElement) || !element.isConnected || this.motion.matches) {
        this.completeEntrance(element);
        return;
      }
      this.seen.add(element);
      this.queuedEntrances.set(element, Math.min(index * 70, 240));
    });
    if (!this.queuedEntrances.size || this.entranceFrame) return;
    this.entranceFrame = requestAnimationFrame(() => {
      this.entranceFrame = 0;
      this.queuedEntrances.forEach((delay, element) => {
        if (!element.isConnected || element.contains(document.activeElement) || this.motion.matches) this.completeEntrance(element);
        else {
          element.style.setProperty('--lookbook-reveal-delay', `${delay}ms`);
          element.classList.add('is-revealed');
        }
      });
      this.queuedEntrances.clear();
    });
  }

  completeEntrance(element) {
    this.observer?.unobserve(element);
    this.observedElements?.delete(element);
    this.queuedEntrances?.delete(element);
    this.seen?.add(element);
    element.classList.remove('is-prepared', 'is-revealed');
    element.style.removeProperty('--lookbook-reveal-delay');
    this.revealElements?.delete(element);
  }

  revealFocused(target) {
    this.revealElements.forEach(element => { if (element.contains(target)) this.completeEntrance(element); });
  }

  finishEntrances() {
    cancelAnimationFrame(this.entranceFrame);
    this.entranceFrame = 0;
    this.observer?.disconnect();
    this.revealElements?.forEach(element => this.completeEntrance(element));
    this.queuedEntrances?.clear();
    this.observedElements?.clear();
  }

  queueParallax() {
    if (document.documentElement?.hasAttribute('data-halloween-incoming')) return;
    if (this.parallaxFrame) return;
    this.parallaxFrame = requestAnimationFrame(() => {
      this.parallaxFrame = 0;
      const enabled = !this.isEditor() && !this.motion.matches;
      this.parallaxLayers.forEach(layer => {
        if (!enabled) {
          layer.style.removeProperty('--lookbook-parallax-y');
          return;
        }
        const bounds = layer.getBoundingClientRect();
        if (!bounds.height || bounds.bottom < 0 || bounds.top > window.innerHeight) return;
        const distance = (window.innerHeight / 2 - bounds.top - bounds.height / 2) / Math.max(window.innerHeight, 1);
        layer.style.setProperty('--lookbook-parallax-y', `${Math.max(-48, Math.min(48, distance * 96))}px`);
      });
    });
  }
}

if (!customElements.get('campaign-lookbook')) customElements.define('campaign-lookbook', CampaignLookbook);
